// Shared chart theme for the Is-Kool 360° dashboard.
// Categorical slots validated for CVD separation, chroma, and contrast on a
// white card surface (dataviz validate_palette.js — all checks pass). Slot
// order is the colorblind-safety mechanism: assign in order, never cycle.
export const SERIES = [
  "#0779A4", // slot 1 — brand teal (nearest chart-legible step of #09617E)
  "#B45309", // slot 2 — warm amber
  "#6D5BD0", // slot 3 — violet
  "#0E7A3D", // slot 4 — green
];

// Status roles carry state (good/bad), never series identity, and always ship
// beside a text label — never color alone.
export const STATUS = {
  good: "#0E8A4A",
  warning: "#B58500",
  serious: "#D9662E",
  critical: "#C03434",
  neutral: "#94a3b8",
};

export const INK = {
  primary: "#0f172a",
  secondary: "#475569",
  muted: "#94a3b8",
  grid: "#e2e8f0",
  axis: "#cbd5e1",
  track: "#f1f5f9",
};

// One identity color per dashboard category — applied via inline style to
// each section's label bar and cards, so the six sections read as distinct
// groups even while scrolling past their boundaries.
export const SECTION_COLORS = {
  overview: "#475569", // neutral slate — a summary, not a category of its own
  finance: SERIES[0], // teal
  students: "#2563eb", // blue
  admissions: SERIES[2], // violet
  staff: SERIES[1], // amber
  operations: SERIES[3], // green
};

export const formatCurrency = (val) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(val || 0);

// Compact form for axis ticks: ₹1.2Cr / ₹4.5L / ₹80K
export const formatCompactCurrency = (val) => {
  const v = Math.abs(val || 0);
  const sign = (val || 0) < 0 ? "-" : "";
  if (v >= 1e7) return `${sign}₹${+(v / 1e7).toFixed(1)}Cr`;
  if (v >= 1e5) return `${sign}₹${+(v / 1e5).toFixed(1)}L`;
  if (v >= 1e3) return `${sign}₹${+(v / 1e3).toFixed(1)}K`;
  return `${sign}₹${Math.round(v)}`;
};

// ─── Meta maps (colors assigned by role) ─────────────────────────────────────

export const FEE_STATUS_META = [
  { key: "paid", label: "Paid", color: STATUS.good },
  { key: "partially_paid", label: "Partially Paid", color: SERIES[0] },
  { key: "pending", label: "Pending", color: STATUS.warning },
  { key: "unpaid", label: "Unpaid", color: STATUS.critical },
];

export const ADMISSION_STATUS_META = [
  { key: "pending", label: "Pending", color: STATUS.warning },
  { key: "approved", label: "Approved", color: STATUS.good },
  { key: "rejected", label: "Rejected", color: STATUS.critical },
  { key: "withdraw", label: "Withdrawn", color: STATUS.neutral },
];

export const GENDER_META = {
  male: { label: "Boys", color: SERIES[0] },
  female: { label: "Girls", color: SERIES[2] },
  others: { label: "Others", color: SERIES[1] },
};

export const PAYMENT_MODE_META = {
  cash: { label: "Cash", color: "green" },
  card: { label: "Card", color: "blue" },
  online: { label: "Online", color: "purple" },
  refund: { label: "Refund", color: "red" },
};

export const LEAVE_TYPE_META = {
  permission: { label: "Permission" },
  leave: { label: "Leave" },
  sick: { label: "Sick" },
};

// ─── ApexCharts base option builders ─────────────────────────────────────────

export const baseChart = (id, type) => ({
  id,
  type,
  toolbar: { show: false },
  zoom: { enabled: false },
  redrawOnParentResize: false,
  fontFamily: "inherit",
});

// Solid hairline grid — never dashed.
export const baseGrid = { borderColor: INK.grid, strokeDashArray: 0 };

export const axisLabelStyle = {
  style: { fontSize: "11px", fontWeight: 500, colors: INK.muted },
};

export const legendBase = {
  position: "bottom",
  fontWeight: 600,
  labels: { colors: INK.secondary },
};

export const areaBase = {
  dataLabels: { enabled: false },
  stroke: { curve: "smooth", width: 2 },
  fill: { type: "solid", opacity: 0.1 },
  grid: baseGrid,
};

export const donutBase = (centerLabel) => ({
  dataLabels: { enabled: false },
  legend: legendBase,
  stroke: { width: 2 },
  plotOptions: {
    pie: {
      donut: {
        size: "68%",
        labels: {
          show: true,
          name: {
            show: true,
            fontSize: "12px",
            fontWeight: 600,
            color: INK.muted,
          },
          value: {
            show: true,
            fontSize: "18px",
            fontWeight: 700,
            color: INK.primary,
          },
          total: {
            show: true,
            label: centerLabel,
            color: INK.secondary,
            fontWeight: 600,
          },
        },
      },
    },
  },
});
